import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  Select,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { useFormContext } from 'react-hook-form';

import { SelectControlProps } from '../../../@types';
import { createLogLocal } from '../../../utils/createLogLocal';

const log = createLogLocal('SelectControl.tsx');

export const SelectControl = React.forwardRef(
  (
    {
      name: fieldName,
      label,
      helper,
      error,
      isDisabled,
      isRequired,
      validateOnBlur = false,
      validateOnChange = false,
      rules,
      list,
      onChangeHandler,
      onBlurHandler,
      onErrorHandler,
      ...props
    }: SelectControlProps,
    ref,
  ) => {
    const {
      register,
      trigger,
      formState: { errors },
    } = useFormContext();

    const { t } = useTranslation();

    const { ref: inputRef, ...rest } = register(fieldName, {
      ...(isRequired && { required: t('requiredField') }),
      ...rules,
      onBlur: async () => {
        if (validateOnBlur) await trigger([fieldName]);
        if (onBlurHandler) onBlurHandler(fieldName as any);
      },
      onChange: async () => {
        if (onChangeHandler) onChangeHandler(fieldName as any);
        if (validateOnChange) await trigger([fieldName]);
      },
    });

    const isInvalid = Boolean(errors[fieldName]?.message);

    React.useEffect(() => {
      if (
        (validateOnBlur || validateOnChange) &&
        onErrorHandler &&
        isInvalid &&
        errors[fieldName]?.message
      ) {
        onErrorHandler({
          [fieldName]: { ...errors[fieldName], key: fieldName },
        });
      }
    }, [
      fieldName,
      isInvalid,
      onErrorHandler,
      errors,
      validateOnBlur,
      validateOnChange,
    ]);

    React.useImperativeHandle(ref, () => ({
      beforeSubmit() {
        log('SelectControl beforeSubmit does nothing.');
      },
      async validate() {
        await trigger([fieldName]);
      },
    }));

    return (
      <FormControl
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        isRequired={isRequired}
        sx={props.sx}
        className={cx('InputContainer')}
      >
        <FormLabel className={cx('InputLabel')}>{label}</FormLabel>
        <InputGroup>
          <Select className={cx('Select')} {...props} {...rest} ref={inputRef}>
            <option value=""> </option>
            {list?.length &&
              list.map(({ value, name }: any, idx: number) => (
                <option key={idx} value={value}>
                  {name}
                </option>
              ))}
          </Select>
        </InputGroup>

        {!isInvalid && (
          <FormHelperText className={cx('InputHelper')}>
            {helper}
          </FormHelperText>
        )}
        {isInvalid && (
          <FormErrorMessage className={cx('InputError')}>
            {errors[fieldName]?.message as string}
          </FormErrorMessage>
        )}
      </FormControl>
    );
  },
);
